import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { AnalitoDoHistoricoResponse } from '@/api/generated/model';
import { cn } from '@/shared/lib/cn';

interface Props {
  analitos: AnalitoDoHistoricoResponse[];
  /** `analitoId` em exibição. */
  valor: string;
  aoEscolher: (analitoId: string) => void;
}

/** O mesmo texto no botão e na lista — mudou num lugar, mudou nos dois. */
function rotuloDoAnalito(analito: AnalitoDoHistoricoResponse) {
  const unidade = analito.unidade ? ` (${analito.unidade})` : '';
  const medicoes = analito.totalMedicoes === 1 ? '1 medição' : `${analito.totalMedicoes} medições`;
  return `${analito.nome}${unidade} · ${analito.exameNome} · ${medicoes}`;
}

/**
 * Minúsculas e sem acento: quem digita "pos acth" tem de achar
 * "Cortisol Basal e Pós-ACTH".
 */
function achatar(texto: string) {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Escolhe qual analito o gráfico de evolução mostra.
 *
 * Era um `Select` do Radix, que só lista — e um paciente com muitos exames
 * chega fácil a dezenas de linhas para rolar. Aqui é combobox: abre com um
 * campo de busca focado, filtra por analito **ou** por exame, e a lista
 * continua navegável pelo teclado. A filtragem é local — o resumo já traz
 * todos os analitos, não há ida ao servidor a cada tecla.
 */
export function SeletorDeAnalito({ analitos, valor, aoEscolher }: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [destaque, setDestaque] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);
  const idLista = useId();

  const selecionado = analitos.find((a) => a.analitoId === valor);

  const filtrados = useMemo(() => {
    const termo = achatar(busca.trim());
    if (!termo) return analitos;
    // Cada palavra tem de aparecer em algum lugar do rótulo: "t4 clia" acha
    // "T4L 2000 (CLIA)" mesmo com a ordem trocada.
    const palavras = termo.split(/\s+/);
    return analitos.filter((a) => {
      const alvo = achatar(rotuloDoAnalito(a));
      return palavras.every((p) => alvo.includes(p));
    });
  }, [analitos, busca]);

  // Ao abrir, o destaque começa em quem já está selecionado; ao digitar,
  // volta para o topo — o primeiro resultado é o candidato mais provável.
  useEffect(() => {
    const i = filtrados.findIndex((a) => a.analitoId === valor);
    setDestaque(i >= 0 ? i : 0);
  }, [filtrados, valor]);

  useEffect(() => {
    if (!aberto) return;

    function aoClicarFora(evento: MouseEvent) {
      if (!containerRef.current?.contains(evento.target as Node)) fechar();
    }

    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  // Setas não podem levar o destaque para fora da parte visível da lista.
  useEffect(() => {
    if (!aberto) return;
    listaRef.current?.children[destaque]?.scrollIntoView({ block: 'nearest' });
  }, [aberto, destaque]);

  function fechar(devolverFoco = false) {
    setAberto(false);
    setBusca('');
    if (devolverFoco) gatilhoRef.current?.focus();
  }

  function escolher(analitoId: string) {
    aoEscolher(analitoId);
    fechar(true);
  }

  function aoTeclar(evento: React.KeyboardEvent) {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();
      if (filtrados.length === 0) return;
      const passo = evento.key === 'ArrowDown' ? 1 : -1;
      setDestaque((i) => (i + passo + filtrados.length) % filtrados.length);
      return;
    }

    if (evento.key === 'Enter') {
      evento.preventDefault();
      const alvo = filtrados[destaque];
      if (alvo) escolher(alvo.analitoId);
      return;
    }

    if (evento.key === 'Escape') {
      evento.preventDefault();
      fechar(true);
    }
  }

  return (
    <div ref={containerRef} className="relative sm:w-96">
      <button
        ref={gatilhoRef}
        type="button"
        role="combobox"
        aria-label="Escolher exame"
        aria-expanded={aberto}
        aria-controls={aberto ? idLista : undefined}
        onClick={() => setAberto((v) => !v)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-left text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <span className="line-clamp-1">
          {selecionado ? rotuloDoAnalito(selecionado) : 'Escolher exame'}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
      </button>

      {aberto && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
            <input
              autoFocus
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={aoTeclar}
              aria-label="Buscar exame"
              aria-controls={idLista}
              placeholder="Buscar exame..."
              className="h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {filtrados.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Nenhum exame encontrado.
            </p>
          ) : (
            <ul ref={listaRef} id={idLista} role="listbox" className="max-h-72 overflow-y-auto p-1">
              {filtrados.map((analito, i) => {
                const atual = analito.analitoId === valor;
                return (
                  <li
                    key={analito.analitoId}
                    role="option"
                    aria-selected={atual}
                    // O mousedown do clique tiraria o foco do campo de busca
                    // antes do onClick rodar; o mouseenter mantém destaque de
                    // teclado e ponteiro em sincronia.
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setDestaque(i)}
                    onClick={() => escolher(analito.analitoId)}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm',
                      i === destaque && 'bg-accent text-accent-foreground',
                    )}
                  >
                    {rotuloDoAnalito(analito)}
                    {atual && <Check className="absolute right-2 h-4 w-4" aria-hidden="true" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
